import type { UseCaseData } from "./types";

export const saasFoundersUseCase: UseCaseData = {
  slug: "saas-founders",
  seo: {
    title: "PassiveAgents for SaaS Founders: Ship features while you sleep",
    description:
      "Use AI coding agents to build features, fix bugs, and maintain your SaaS product around the clock while you focus on customers, sales, and strategy.",
    canonical:
      "https://www.passiveagents.com/use-cases/ai-coding-agents-for-saas-founders",
  },
  breadcrumbs: [
    { label: "Home", href: "/home" },
    { label: "Use Cases", href: "/use-cases" },
    { label: "SaaS Founders" },
  ],
  h1: "Ship features while your team is asleep",
  shortAnswer:
    "SaaS founders spend too much time writing code and too little time talking to customers, closing deals, and planning the roadmap. PassiveAgents turns Claude Code, Codex, OpenCode, and Gemini CLI into a coordinated team that keeps building after you stop. You create the tasks. Your agents work through them in parallel. You review what they ship. The cycle continues whether you are in a meeting, on a call, or in bed.",

  whoThisIsFor: {
    headline: "Who this is for",
    audiences: [
      {
        type: "Solo SaaS founders",
        description:
          "You are the only developer. Every bug fix means pausing a feature. Every customer support ticket derails your build. You need code written while you handle everything else.",
      },
      {
        type: "Small SaaS teams",
        description:
          "Your team is 2 to 5 people. You cannot afford to stop building while someone investigates a production issue. You want your backlog moving forward even when the team is async.",
      },
      {
        type: "Bootstrapped founders",
        description:
          "You do not have venture capital to hire a dozen engineers. You need a force multiplier that works for the cost of API credits.",
      },
    ],
  },

  commonProblems: [
    {
      problem: "Too much context switching",
      detail:
        "Every time you jump from building a feature to fixing a bug to triaging a support ticket, you lose 15 to 30 minutes of productive focus. By the end of the day, you have started five things and finished one.",
    },
    {
      problem: "Backlog grows faster than you can ship",
      detail:
        "Your GitHub Issues or Linear board fills up faster than you can close items. Every new feature request pushes existing work further out. The list never shrinks.",
    },
    {
      problem: "You cannot scale without hiring",
      detail:
        "Hiring is expensive and slow. Even a junior developer costs thousands per month. You need output to scale without your burn rate scaling with it.",
    },
    {
      problem: "Shipping stops when you stop",
      detail:
        "When you close your laptop at night, the code stops too. A full day of lost time is 8 hours of unshipped work, every day.",
    },
  ],

  howPassiveAgentsHelps: [
    {
      feature: "Multiple agents work in parallel",
      benefit:
        "While one agent builds a new API endpoint, another fixes a reported bug, and a third writes tests. Three tasks move forward at the same time, not one.",
    },
    {
      feature: "Agents work while you are away",
      benefit:
        "Assign tasks before you go to sleep. Wake up to a morning summary of what shipped, what got stuck, and what needs your review.",
    },
    {
      feature: "Works with your existing tools",
      benefit:
        "Connect the coding agents you already use: Claude Code, Codex, OpenCode, and Gemini CLI. No new workflows to learn.",
    },
    {
      feature: "Project board keeps everything organized",
      benefit:
        "Every task lives on a Kanban board. You see what is queued, in progress, and ready for review. No more digging through terminal history to find out what happened.",
    },
  ],

  exampleWorkflows: [
    {
      title: "Overnight feature sprint",
      steps: [
        "Create tasks for three features on your roadmap before bed.",
        "Agents pick up the work and start building in parallel.",
        "Each agent works on its own task worktree inside your repo.",
        "Wake up to completed feature branches ready for review.",
        "Review diffs, leave feedback, and merge what ships.",
      ],
    },
    {
      title: "Bug triage and fix rotation",
      steps: [
        "Drop new bug reports from customers into the project board.",
        "An agent picks each bug, reproduces it, and writes a fix.",
        "Agents flag bugs they cannot resolve for human review.",
        "Review the fixes, merge them, and deploy.",
        "Customers get a fix without you dropping feature work.",
      ],
    },
    {
      title: "Backlog cleanup day",
      steps: [
        "Pull the oldest 10 items from your backlog into the board.",
        "Agents evaluate each one, estimate effort, and start the feasible ones.",
        "Impossible or unclear tasks get flagged with specific questions.",
        "You answer questions and the agents proceed.",
        "By end of week, the backlog is shorter, not longer.",
      ],
    },
  ],

  whatHumansReview: [
    "Architecture decisions that affect the whole product",
    "Security-sensitive code changes",
    "Customer-facing copy and messaging",
    "Database migrations and schema changes",
    "API contract changes that affect integrations",
  ],

  whyDifferent: [
    {
      point: "It is not a code generator you prompt once",
      detail:
        "You do not type a prompt, get code, and start over. PassiveAgents keeps agents working on tasks until they are done. The agents persist, take feedback, and improve over time.",
    },
    {
      point: "It coordinates agents you already have",
      detail:
        "PassiveAgents does not replace Claude Code or Codex. It orchestrates them. Your existing setups, API keys, and preferences carry over. The only difference is your agents now work as a team.",
    },
    {
      point: "You stay in control",
      detail:
        "Agents run on your laptop and PassiveAgents handles coordination — your source files stay on your machine. Note: the agent runtimes you connect (Claude Code, Codex, etc.) may send prompts or context to their own providers. You review every change before it ships. Agents do not deploy to production without your signoff.",
    },
    {
      point: "Built for continuous work, not one-off tasks",
      detail:
        "PassiveAgents is designed for the kind of work a SaaS founder faces every day: a stream of features, bugs, refactors, and maintenance. Not a single prompt, but an ongoing workflow.",
    },
  ],

  setupOptions: [
    {
      title: "Free Trial",
      description:
        "Full access for 7 days. 6 agents, 20 active tasks, all runtimes. No credit card required.",
      cta: "Start free trial",
      href: "/pricing",
    },
    {
      title: "Pro Plan",
      description:
        "$20/month for 20 agents and 200 active tasks. Cancel anytime.",
      cta: "Subscribe",
      href: "/pricing?checkout=monthly",
    },
  ],

  faq: [
    {
      question:
        "Do I need to know how to set up coding agents to use PassiveAgents?",
      answer:
        "You need at least one coding agent installed (Claude Code, Codex, OpenCode, or Gemini CLI). The PassiveAgents installer guides you through setting them up. Most founders are up and running in under 10 minutes.",
    },
    {
      question: "Will agents break my production code?",
      answer:
        "Agents work in isolated task worktrees inside your repo. They do not touch your main branch. Every change goes through your normal review process before it ships.",
    },
    {
      question: "How is this different from Copilot or Cursor?",
      answer:
        "Copilot and Cursor assist you while you code. PassiveAgents lets agents code without you. They work on tasks you assign, in the background, using the same tools you would use yourself.",
    },
    {
      question:
        "Can I use PassiveAgents with my existing Linear or GitHub Issues?",
      answer:
        "Not yet. You create tasks directly in the PassiveAgents project board. Import from other tools is on the roadmap.",
    },
    {
      question: "What happens to my data?",
      answer:
        "Your code and API keys stay on your machine. PassiveAgents handles the coordination layer: task tracking, agent scheduling, and the web interface. We never see your source code.",
    },
  ],

  cta: {
    headline: "Start shipping while you sleep",
    description:
      "Turn your backlog into a team of agents that work in parallel, around the clock. Set up in 10 minutes.",
    buttonText: "Start free trial",
    buttonHref: "/pricing",
  },
};
